<template>
  <line-chart :chart-data="chartData" :chart-options="chartOptions" style="min-height: 440px" />
</template>

<script>
  import { Line as LineChart } from 'vue-chartjs'
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    CategoryScale,
    PointElement,
    TimeScale,
    LogarithmicScale,
  } from 'chart.js'

  import 'chartjs-adapter-date-fns'
  import ReviewsFinalRatingStatus from '@/models/enums/ReviewsFinalRatingStatus'
  import { positioningTooltip } from '@/util/chart'
  import { numWord } from '@/util/string'

  ChartJS.register(
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    LogarithmicScale,
    CategoryScale,
    PointElement,
    TimeScale,
  )

  export default {
    name: 'RatingsChart',
    components: { LineChart },
    props: {
      items: {
        type: Array,
        default: () => [],
      },
      timeUnit: {
        type: String,
        default: 'day',
      },
    },
    computed: {
      chartOptions () {
        return {
          responsive: true,
          maintainAspectRatio: false,
          parsing: {
            xAxisKey: 'date',
            yAxisKey: 'normalizedRating',
          },
          scales: {
            x: {
              type: 'time',
              time: {
                unit: this.timeUnit,
                tooltipFormat: 'yyyy-MM-dd',
                displayFormats: {
                  day: 'yyyy-MM-dd',
                  week: 'yyyy-MM-dd',
                  month: 'yyyy-MM',
                },
              },
              title: {
                display: true,
                text: this.datasetLabel,
                align: 'end',
                color: '#000',
                font: {
                  size: 14,
                },
              },
              grid: {
                display: true,
                drawOnChartArea: false,
              },
              ticks: {
                callback: (value) => {
                  const date = new Date(value)

                  return this.timeUnit === 'month'
                    ? date.toLocaleDateString('ru', { month: 'short', year: 'numeric' })
                    : date.toLocaleDateString('ru', { day: 'numeric', month: 'short' })
                },
              },
            },
            y: {
              min: this.beginAtOne ? 1 : undefined,
              ticks: {
                stepSize: 1,
                callback: function (value) {
                  if (value <= 3) return value
                  if (value > 8) return value - 4
                  if (value === 8 || value === 7) return null
                  if (value > 5) return value - 2
                  if (value <= 5) return null
                  return value
                },
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: 'nearest',
              external: this.externalTooltipHandler,
            },
          },
        }
      },
      chartData () {
        return {
          labels: this.items.map(item => item.date),
          datasets: [
            {
              backgroundColor: '#FF9D00',
              borderColor: '#FF9D00',
              data: this.datasetData,
              cubicInterpolationMode: 'monotone',
              pointRadius: 2,
              pointHoverRadius: 8,
            },
          ],
        }
      },
      datasetData () {
        return this.items.map(item => {
          item.normalizedRating = item.rating

          if (item.rating >= 3) {
            item.normalizedRating = (item.rating - 3) * 3 + 3
          }

          return item
        })
      },
      beginAtOne () {
        if (!this.items.length) {
          return
        }

        let min = this.items[0].rating

        this.items.forEach(item => {
          if (min > item.rating) min = item.rating
        })

        return min === 2
      },
      datasetLabel () {
        if (!this.items.length) {
          return ''
        }

        if (this.items.length === 1) {
          return new Date(this.items[0].date).toLocaleDateString('ru', { day: 'numeric', month: 'numeric', year: 'numeric' })
        }

        const startDate = new Date(this.items[0].date).toLocaleDateString('ru', { day: 'numeric', month: 'numeric', year: 'numeric' })
        const endDate = new Date(this.items[this.items.length - 1].date).toLocaleDateString('ru', { day: 'numeric', month: 'numeric', year: 'numeric' })

        return startDate + ' - ' + endDate
      },
    },
    methods: {
      externalTooltipHandler (context) {
        const { chart, tooltip } = context
        const tooltipEl = this.createTooltip(chart)

        if (tooltip.opacity === 0) {
          tooltipEl.style.opacity = 0
          return
        }

        if (tooltip.dataPoints[0]) {
          // Remove old children
          while (tooltipEl.firstChild) {
            tooltipEl.firstChild.remove()
          }

          tooltipEl.appendChild(this.createInfoContainer(tooltip.dataPoints[0]))
          tooltipEl.appendChild(this.createIconContainer())
        }

        positioningTooltip(chart, tooltip, tooltipEl)
      },
      createTooltip (chart) {
        let tooltipEl = chart.canvas.parentNode.querySelector('div')

        if (!tooltipEl) {
          tooltipEl = document.createElement('div')
          tooltipEl.classList.add('tooltip')
          tooltipEl.style.position = 'absolute'
          tooltipEl.style.display = 'flex'
          tooltipEl.style.alignItems = 'center'
          tooltipEl.style.background = '#333232'
          tooltipEl.style.borderRadius = '5px'
          tooltipEl.style.color = 'white'
          tooltipEl.style.opacity = 1
          tooltipEl.style.pointerEvents = 'none'
          tooltipEl.style.position = 'absolute'
          tooltipEl.style.transition = 'all .1s ease'
          tooltipEl.style.padding = '5px'

          chart.canvas.parentNode.appendChild(tooltipEl)
        }

        return tooltipEl
      },
      createInfoContainer (dataPoint) {
        const infoContainer = document.createElement('div')

        const dateWrapper = document.createElement('div')
        const date = new Date(dataPoint.label).toLocaleDateString('ru', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        })
        dateWrapper.appendChild(document.createTextNode(date))

        infoContainer.appendChild(dateWrapper)
        infoContainer.appendChild(this.createRatingContainer(dataPoint))

        return infoContainer
      },
      createRatingContainer (dataPoint) {
        const data = dataPoint.dataset.data[dataPoint.dataIndex]

        const ratingContainer = document.createElement('div')
        ratingContainer.style.display = 'flex'
        ratingContainer.style.alignItems = 'center'
        ratingContainer.style.whiteSpace = 'nowrap'

        const rating = document.createElement('span')
        rating.appendChild(document.createTextNode(data.rating))

        if (data.status !== ReviewsFinalRatingStatus.NotChanged) {
          if (data.status === ReviewsFinalRatingStatus.Increased) {
            rating.style.color = '#6AAD00'
            ratingContainer.appendChild(this.createArrowUpIcon())
          } else {
            rating.style.color = '#E92663'
            ratingContainer.appendChild(this.createArrowDownIcon())
          }
        }

        ratingContainer.appendChild(rating)

        if (data.count) {
          rating.style.marginRight = '5px'

          const ratingCount = document.createElement('span')
          ratingCount.style.color = '#9B9B9B'
          ratingCount.appendChild(document.createTextNode(`(${data.count} ${numWord(data.count, 'оценка', 'оценки', 'оценок')})`))

          ratingContainer.appendChild(ratingCount)
        }

        return ratingContainer
      },
      createArrowUpIcon () {
        const icon = document.createElement('span')
        icon.classList.add('mdi', 'mdi-arrow-up')
        // icon.style.marginRight = '2px'
        icon.style.color = '#6AAD00'

        return icon
      },
      createArrowDownIcon () {
        const icon = document.createElement('span')
        icon.classList.add('mdi', 'mdi-arrow-down')
        // icon.style.marginRight = '2px'
        icon.style.color = '#E92663'

        return icon
      },
      createIconContainer () {
        const iconContainer = document.createElement('div')
        const icon = document.createElement('img')
        icon.src = '/img/primary-star.svg'
        icon.style.marginLeft = '20px'
        icon.style.width = '20px'
        icon.style.height = '19px'
        iconContainer.appendChild(icon)

        return iconContainer
      },
    },
  }
</script>
